import { NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, Plug, FolderTree, ScrollText, LogOut, Zap, Users, Settings } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const baseItems = [
  { to: "/dashboard", label: "Dashboard", short: "Home", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/integrations", label: "Integrations", short: "Setup", icon: Plug, testid: "nav-integrations" },
  { to: "/drive", label: "Drive Browser", short: "Drive", icon: FolderTree, testid: "nav-drive" },
  { to: "/logs", label: "Attempt Logs", short: "Logs", icon: ScrollText, testid: "nav-logs" },
];

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const items = [
    ...baseItems,
    ...(user?.is_admin
      ? [
          { to: "/admin/users", label: "Users", short: "Users", icon: Users, testid: "nav-admin" },
          { to: "/admin/settings", label: "Settings", short: "Config", icon: Settings, testid: "nav-admin-settings" },
        ]
      : []),
  ];

  return (
    <div className="min-h-screen flex flex-col bg-white">
      {/* BETA banner */}
      <div className="w-full bg-gradient-to-r from-amber-400 to-orange-400 text-amber-950 text-center text-[11px] sm:text-xs font-bold tracking-wide py-1.5 px-3" data-testid="beta-banner">
        BETA VERSION · FOR TEST USERS ONLY
      </div>

      <div className="flex-1 flex flex-col md:flex-row">
        {/* Mobile top bar */}
        <header
          className="md:hidden sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-zinc-200 flex items-center justify-between px-4 h-14"
          data-testid="mobile-topbar"
        >
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-md bg-gradient-to-br from-fuchsia-500 to-cyan-500 text-white grid place-items-center">
              <Zap className="w-3.5 h-3.5" />
            </div>
            <div className="font-display font-black tracking-tight text-slate-900 text-sm">PostQik</div>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="w-8 h-8 rounded-full bg-zinc-100 text-slate-700 grid place-items-center text-xs font-semibold"
                aria-label="Open account menu"
                data-testid="mobile-account-menu"
              >
                {(user?.email || "?").slice(0, 1).toUpperCase()}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <DropdownMenuLabel className="truncate text-xs text-slate-500 font-normal">
                Signed in as
                <div className="text-slate-900 font-medium mt-0.5 truncate">{user?.email}</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} data-testid="logout-button-mobile" className="text-slate-700">
                <LogOut className="w-4 h-4" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        {/* Desktop sidebar */}
        <aside
          className="hidden md:flex md:flex-col md:w-64 md:min-h-[calc(100vh-28px)] border-r border-zinc-200 bg-white"
          data-testid="app-sidebar"
        >
          <div className="p-6 border-b border-zinc-200">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-fuchsia-500 to-cyan-500 text-white grid place-items-center">
                <Zap className="w-4 h-4" />
              </div>
              <div>
                <div className="font-display font-black tracking-tight text-slate-900">PostQik</div>
                <div className="text-xs text-slate-500">Drive → TikTok</div>
              </div>
            </div>
          </div>
          <nav className="flex flex-col gap-1 p-3 flex-1">
            {items.map((it) => {
              const Icon = it.icon;
              return (
                <NavLink
                  key={it.to}
                  to={it.to}
                  data-testid={it.testid}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                      isActive
                        ? "bg-zinc-900 text-white"
                        : "text-slate-700 hover:bg-zinc-100 hover:text-slate-900"
                    }`
                  }
                >
                  <Icon className="w-4 h-4" />
                  <span>{it.label}</span>
                </NavLink>
              );
            })}
          </nav>
          <div className="p-3 border-t border-zinc-200">
            <div className="text-xs text-slate-500 mb-2 truncate" data-testid="current-user-email">
              {user?.email}
            </div>
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              onClick={handleLogout}
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4" /> Sign out
            </Button>
          </div>
        </aside>

        {/* Main content */}
        <main className="flex-1 min-w-0 pb-20 md:pb-0">
          <div className="max-w-6xl mx-auto px-4 py-4 sm:px-6 sm:py-6 md:px-8 md:py-8 lg:px-10 lg:py-10">
            {children}
          </div>
        </main>
      </div>

      {/* Mobile bottom tab bar */}
      <nav
        className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur border-t border-zinc-200 flex items-stretch"
        data-testid="mobile-bottom-nav"
        style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
      >
        {items.map((it) => {
          const Icon = it.icon;
          return (
            <NavLink
              key={it.to}
              to={it.to}
              data-testid={`${it.testid}-mobile`}
              className={({ isActive }) =>
                `flex-1 flex flex-col items-center justify-center gap-0.5 py-2.5 text-[11px] font-medium transition-colors ${
                  isActive ? "text-zinc-900" : "text-slate-500 hover:text-slate-800"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <div className={`w-8 h-8 rounded-md grid place-items-center transition-colors ${isActive ? "bg-zinc-900 text-white" : "text-slate-500"}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <span>{it.short}</span>
                </>
              )}
            </NavLink>
          );
        })}
      </nav>
    </div>
  );
}
